import { useLazyQuery, useQuery } from "@apollo/client";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useImperativeHandle, forwardRef, useMemo } from "react";
import { usePrevious } from "react-use";
import {
  supplyChainBookingFieldSettingsQuery,
  supplyChainPartnerLocationsQuery,
  getLocationOptions,
} from "../BookingForm";
import { type SectionId, type MetadataField, BASE_COLUMN_IDS, type BaseColumnId } from "../BookingForm/types";
import { BookingStatusBadge } from "../BookingStatusBadge";
import { BookingActionMenu } from "./BookingActionMenu";
import { BookingsFilters, statusOptions } from "./BookingsFilters";
import { isSortable, metadataColumns } from "./columnDefinitions";
import { BOOKINGS_SORT_FIELDS, type BookingsSortField } from "./constants";
import { type ColumnGroup } from "@/components/ColumnVisibilityToggle";
import { useUrlTableSorting } from "@/components/DataTable/useUrlTableSorting";
import { FilterBadge, FilterBadgeGroup } from "@/components/FilterBadges";
import { getFilterBadgeItems, toggleFilterState } from "@/components/FilterBadges/utils";
import {
  pageInfoFragment,
  Pagination,
  type PaginationProperties,
  type ResetPagination,
  usePagination,
} from "@/components/Pagination";
import { Table } from "@/components/Table";
import { Heading4 } from "@/components/Typography";
import { Card } from "@/components/ui/card";
import { graphql, type VariablesOf, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { usePageSizePreference } from "@/hooks/use-page-size-preference";
import { useSortPreference } from "@/hooks/use-sort-preference";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { toDisplayDate } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatName } from "@/lib/utils/formatting";
import { type BookingFilters } from "@/routes/(authed)/_layout/bookings.route";

export type SortingField = NonNullable<NonNullable<VariablesOf<typeof supplyChainBookingsQuery>["sortBy"]>[0]>["field"];
interface BookingsTableProps {
  filters: BookingFilters;
  setFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  inactiveFilters: BookingFilters;
  setInactiveFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
}

export interface BookingsTableHandle {
  resetPagination: ResetPagination;
}

export type SupplyChainBooking = NonNullable<
  NonNullable<NonNullable<ResultOf<typeof supplyChainBookingsQuery>["supplyChainBookings"]>["edges"]>[number]
>["node"];

const { accessor } = createColumnHelper<SupplyChainBooking>();
const bookingActionsColumn = accessor((row) => row?.id, {
  id: "actions",
  header: "",
  size: 50,
  enableSorting: false,
  enableHiding: false,
  enableResizing: false,
  cell: (info) => {
    const bookingId = info.getValue();
    return bookingId ? <BookingActionMenu bookingId={bookingId} /> : null;
  },
});
const BOOKINGS_TABLE_PINNING = { alwaysPinnedRight: ["actions"] } as const;
const routeApi = getRouteApi("/(authed)/_layout/bookings");

export const BookingsTable = forwardRef<BookingsTableHandle, BookingsTableProps>(
  ({ filters, setFilters, inactiveFilters, setInactiveFilters }, ref) => {
    const navigate = routeApi.useNavigate();

    const [getBookings, { loading, data, error, refetch }] = useLazyQuery(supplyChainBookingsQuery, {
      fetchPolicy: "cache-and-network",
    });
    const { toast } = useToast();
    const { activeBusinessUnitId } = useActiveBusinessUnit();
    const {
      dateOpenedAfter,
      dateOpenedBefore,
      status,
      sortField,
      sortDesc,
      pageSize,
      name,
      lotNumber,
      accountNumber,
      sourceIds,
      destinationIds,
    } = routeApi.useSearch();
    const [pageSizePreference, setPageSizePreference] = usePageSizePreference("bookingsTable");
    const [sortPreference, setSortPreference] = useSortPreference("bookingsTable");

    // Fetch field settings to determine which metadata columns to show
    const { data: fieldSettingsData } = useQuery(supplyChainBookingFieldSettingsQuery, {
      variables: { id: activeBusinessUnitId || "" },
      skip: !activeBusinessUnitId,
    });

    const fieldSettings = removeNothings(fieldSettingsData?.supplyChainBookingFieldSettings || []);

    const lotNumberSetting = fieldSettings.find((field) => field.field === "LOT_NUMBER");
    const usesLotNumber = lotNumberSetting?.isVisible ?? false;

    const accountNumberSetting = fieldSettings.find((field) => field.field === "ACCOUNT_NUMBER");
    const usesAccountNumber = accountNumberSetting?.isVisible ?? false;

    const pagination = usePagination({
      totalCount: data?.supplyChainBookings?.numSupplyChainBookings || 0,
      pageInfo: data?.supplyChainBookings?.pageInfo,
      loading,
      urlPageSize: pageSize,
      localStoragePageSize: pageSizePreference,
      onUrlPageSizeChange: (newPageSize) => {
        void navigate({ search: (prev) => ({ ...prev, pageSize: newPageSize }) });
      },
      onPageSizePreferenceChange: setPageSizePreference,
    });

    useImperativeHandle(ref, () => ({
      resetPagination: pagination.controls.handleResetPagination,
    }));

    const { sortBy, onSortingChange, sorting } = useUrlTableSorting<BookingsSortField>({
      initialSort: { id: "PLANNED_SHIPPING_DATE", desc: true },
      validFields: BOOKINGS_SORT_FIELDS,
      sortParams: { sortField, sortDesc },
      localStorageSort: sortPreference,
      onSortParamsChange: (field, desc) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            sortField: field,
            sortDesc: desc,
          }),
        });
      },
      onLocalStorageSortChange: setSortPreference,
    });

    const bookings = removeNothings(
      data?.supplyChainBookings?.edges.map((edge) => {
        return edge?.node;
      }) || [],
    );

    useEffect(() => {
      const variables: VariablesOf<typeof supplyChainBookingsQuery> = {
        first: pagination.paginationQueryVariables.first,
        last: pagination.paginationQueryVariables.last,
        after: pagination.paginationQueryVariables.after,
        before: pagination.paginationQueryVariables.before,
        businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
        sortBy,
        plannedShippingDateBefore: dateOpenedBefore,
        plannedShippingDateAfter: dateOpenedAfter,
        // An empty array in the URL means "cleared on purpose" for the route's
        // benefit; the API is sent no status filter rather than an empty list.
        status: status?.length ? status : undefined,
        name,
        lotNumber,
        accountNumberIstartswith: accountNumber,
        sourceIn: sourceIds,
        destinationIn: destinationIds,
      };

      const fetchData = async () => {
        await getBookings({
          variables,
        });
      };

      fetchData().catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: getErrorMessage(error) || "There was a problem with your request.",
          action: { label: "Try again", onClick: () => refetch() },
        });
      });
    }, [
      pagination.paginationQueryVariables.first,
      pagination.paginationQueryVariables.last,
      pagination.paginationQueryVariables.after,
      pagination.paginationQueryVariables.before,
      getBookings,
      toast,
      refetch,
      activeBusinessUnitId,
      sortBy,
      dateOpenedBefore,
      dateOpenedAfter,
      status,
      name,
      lotNumber,
      accountNumber,
      sourceIds,
      destinationIds,
    ]);

    const prevNumBookings = usePrevious(data?.supplyChainBookings?.numSupplyChainBookings);
    const displayNumBookings =
      loading && prevNumBookings ? prevNumBookings : data?.supplyChainBookings?.numSupplyChainBookings || 0;

    const columns = useBookingTableColumns(fieldSettings);

    const columnGroups = useMemo(() => {
      const bookingDetailsGroup: ColumnGroup = {
        id: "booking_details",
        title: "Booking Details",
        columnIds: [...BASE_COLUMN_IDS, "NUM_NON_ARCHIVED_DOCUMENTS"],
      };

      // Group metadata columns by their meta.group property
      const groupMap = new Map<SectionId, string[]>();
      const groupTitles: Record<SectionId, string> = {
        lot_inventory: "Lot & Inventory Details",
        dates: "Dates & Scheduling",
        financial: "Financial Details",
        weight: "Weight & Quality Information",
        source: "Source & Documentation",
        transportation: "Transportation & Services",
      };

      metadataColumns.forEach((column) => {
        if (!column.id) return;
        const group = (column.meta as { group?: SectionId } | undefined)?.group;
        if (group) {
          if (!groupMap.has(group)) {
            groupMap.set(group, []);
          }
          groupMap.get(group)!.push(column.id);
        }
      });

      const metadataGroups: ColumnGroup[] = Array.from(groupMap.entries()).map(([groupId, columnIds]) => ({
        id: groupId,
        title: groupTitles[groupId] || groupId,
        columnIds,
      }));

      return [bookingDetailsGroup, ...metadataGroups];
    }, []);

    return (
      <Card className={"m-4 p-4 bg-transparent overflow-hidden"}>
        <Heading4>Bookings ({displayNumBookings})</Heading4>
        <Table.Root
          data={bookings}
          columns={columns}
          loading={loading && !data}
          error={error?.message}
          tableId="bookingsTable"
          sortConfig={{ onSortingChange, sorting }}
          columnPinningOptions={BOOKINGS_TABLE_PINNING}
        >
          <Table.Header className="px-0">
            <FilterBadges
              handleResetPagination={pagination.controls.handleResetPagination}
              setFilters={setFilters}
              filters={filters}
              inactiveFilters={inactiveFilters}
              setInactiveFilters={setInactiveFilters}
            />
            <Table.ColumnToggle groups={columnGroups} />
            <BookingsFilters
              filters={filters}
              setFilters={setFilters}
              setInactiveFilters={setInactiveFilters}
              usesLotNumber={usesLotNumber}
              usesAccountNumber={usesAccountNumber}
            />
          </Table.Header>

          <Table.Body<SupplyChainBooking>
            onRowClick={(row) => {
              if (!row) return;

              void navigate({
                to: "/bookings/$bookingId",
                params: { bookingId: row.id },
              });
            }}
            pageSize={Number(pagination.paginationInfo.pageSize)}
          />

          <Table.Footer className="flex justify-end">
            <Pagination pagination={pagination} />
          </Table.Footer>
        </Table.Root>
      </Card>
    );
  },
);

BookingsTable.displayName = "BookingsTable";

function useBookingTableColumns(
  fieldSettings: Array<{ field: MetadataField; isVisible: boolean; isRequired: boolean }>,
) {
  const visibilityMap = useMemo(
    () => new Map(fieldSettings.map((setting) => [setting.field, setting.isVisible])),
    [fieldSettings],
  );

  return useMemo(() => {
    const baseColumns = [
      accessor((row) => row?.name, {
        id: "NAME" satisfies BaseColumnId,
        header: "Booking Name",
        cell: (info) => info.getValue(),
        enableSorting: isSortable("NAME"),
      }),
      accessor((row) => row?.plannedShippingDate, {
        id: "PLANNED_SHIPPING_DATE" satisfies BaseColumnId,
        header: "Planned Shipping Date",
        cell: (info) => {
          const plannedShippingDate = info.getValue();
          return plannedShippingDate ? toDisplayDate(plannedShippingDate) : "\u2014";
        },
        enableSorting: isSortable("PLANNED_SHIPPING_DATE"),
      }),
      accessor((row) => row?.createdAt, {
        id: "CREATED_AT" satisfies BaseColumnId,
        header: "Date Created",
        cell: (info) => {
          const createdAt = info.getValue();
          return createdAt ? toDisplayDate(createdAt) : "\u2014";
        },
        enableSorting: isSortable("CREATED_AT"),
      }),
      accessor((row) => row?.accountManager, {
        id: "ACCOUNT_MANAGER_LAST_NAME" satisfies BaseColumnId,
        header: "Booking Manager",
        cell: (info) => {
          const accountManager = info.getValue();
          if (!accountManager?.user) return "\u2014";
          return formatName({
            firstName: accountManager.user.firstName,
            lastName: accountManager.user.lastName,
          });
        },
        enableSorting: isSortable("ACCOUNT_MANAGER_LAST_NAME"),
      }),
      accessor((row) => row?.numberOfHead, {
        id: "NUMBER_OF_HEAD" satisfies BaseColumnId,
        header: "Number of Head",
        cell: (info) => {
          const numberOfHead = info.getValue();
          return numberOfHead ? numberOfHead.toString() : "\u2014";
        },
        enableSorting: isSortable("NUMBER_OF_HEAD"),
      }),
      accessor((row) => row?.source, {
        id: "SOURCE_PARTNER_BUSINESS_NAME" satisfies BaseColumnId,
        header: "Source",
        cell: (info) => {
          const source = info.getValue();
          if (!source) return "\u2014";
          return source.supplyChainPartner?.businessName || "\u2014";
        },
        enableSorting: isSortable("SOURCE_PARTNER_BUSINESS_NAME"),
      }),
      accessor((row) => row?.destination, {
        id: "DESTINATION_PARTNER_BUSINESS_NAME" satisfies BaseColumnId,
        header: "Destination",
        cell: (info) => {
          const destination = info.getValue();
          if (!destination) return "\u2014";
          return destination.supplyChainPartner?.businessName || "\u2014";
        },
        enableSorting: isSortable("DESTINATION_PARTNER_BUSINESS_NAME"),
      }),
      accessor((row) => row?.status, {
        id: "STATUS" satisfies BaseColumnId,
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          return status ? <BookingStatusBadge status={status} /> : "\u2014";
        },
        enableSorting: isSortable("STATUS"),
      }),
      accessor((row) => row?.numNonArchivedDocuments, {
        id: "NUM_NON_ARCHIVED_DOCUMENTS",
        header: "No. of Documents",
        cell: (info) => {
          const numNonArchivedDocuments = info.getValue();
          return numNonArchivedDocuments ? numNonArchivedDocuments.toString() : "\u2014";
        },
        enableSorting: false,
      }),
    ];

    // Filter metadata columns based on visibility settings
    const visibleMetadataColumns = metadataColumns.filter((col) => {
      // Use fieldId from meta if available, otherwise use the column id
      const fieldId = (col.meta as { fieldId?: MetadataField })?.fieldId || col.id;
      return visibilityMap.get(fieldId as MetadataField) !== false;
    });

    return [...baseColumns, ...visibleMetadataColumns, bookingActionsColumn];
  }, [visibilityMap]);
}

type ArrayFilterKey = "status" | "sourceIds" | "destinationIds";

function FilterBadges({
  filters,
  setFilters,
  inactiveFilters,
  setInactiveFilters,
  handleResetPagination,
}: {
  filters: BookingFilters;
  setFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  inactiveFilters: BookingFilters;
  setInactiveFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  handleResetPagination: PaginationProperties["controls"]["handleResetPagination"];
}) {
  const navigate = useNavigate({ from: "/bookings/" });
  const { activeBusinessUnitId, activeBusinessUnitCountryCode } = useActiveBusinessUnit();

  // Fetch partner data to display names in badges
  const { data: supplyChainPartnerData } = useQuery(supplyChainPartnerLocationsQuery, {
    variables: {
      businessUnit: activeBusinessUnitId || "",
      status: ["ACTIVE"],
      sortBy: [{ field: "BUSINESS_NAME", desc: false }],
    },
    skip: !activeBusinessUnitId,
  });

  const partnerOptions = getLocationOptions({
    edges: supplyChainPartnerData?.supplyChainPartners?.edges || [],
    businessUnitCountryCode: activeBusinessUnitCountryCode,
  });

  const handleRemoveName = () => {
    setFilters((prevFilters) => ({ ...prevFilters, name: undefined }));
    handleResetPagination();
    void navigate({ search: (prev) => ({ ...prev, name: undefined }) });
  };

  const handleRemoveLotNumber = () => {
    setFilters((prevFilters) => ({ ...prevFilters, lotNumber: undefined }));
    handleResetPagination();
    void navigate({ search: (prev) => ({ ...prev, lotNumber: undefined }) });
  };

  const handleRemoveAccountNumber = () => {
    setFilters((prevFilters) => ({ ...prevFilters, accountNumber: undefined }));
    handleResetPagination();
    void navigate({ search: (prev) => ({ ...prev, accountNumber: undefined }) });
  };

  const handleRemoveDateRange = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dateOpenedAfter: undefined,
      dateOpenedBefore: undefined,
    }));

    handleResetPagination();

    void navigate({ search: (prev) => ({ ...prev, dateOpenedAfter: undefined, dateOpenedBefore: undefined }) });
  };

  // The three multi-value filters behave identically, so they share one pair of
  // handlers keyed by filter. An inactive value stays listed but stops
  // narrowing the results; removing it drops it from the list entirely.
  const applyArrayFilter = ({
    key,
    active,
    inactive,
  }: {
    key: ArrayFilterKey;
    active: string[];
    inactive: string[];
  }) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: active }));
    setInactiveFilters((prevInactive) => ({ ...prevInactive, [key]: inactive }));

    handleResetPagination();

    void navigate({
      search: (prev) => ({
        ...prev,
        // Status keeps an explicit empty array so the route can tell "all
        // turned off" from "first visit" and leave the defaults alone.
        [key]: key === "status" ? active : active.length ? active : undefined,
      }),
    });
  };

  const handleRemoveValue = (key: ArrayFilterKey) => (valueToRemove: string) => {
    applyArrayFilter({
      key,
      active: (filters[key] ?? []).filter((v) => v !== valueToRemove),
      inactive: (inactiveFilters[key] ?? []).filter((v) => v !== valueToRemove),
    });
  };

  const handleToggleValue = (key: ArrayFilterKey) => (valueToToggle: string) => {
    const result = toggleFilterState({
      value: valueToToggle,
      activeFilters: filters[key],
      inactiveFilters: inactiveFilters[key],
    });

    applyArrayFilter({ key, active: result.activeFilters ?? [], inactive: result.inactiveFilters ?? [] });
  };

  const handleRemoveAll = (key: ArrayFilterKey) => () => applyArrayFilter({ key, active: [], inactive: [] });

  return (
    <div className="min-h-11 flex items-center gap-2 pt-4 flex-wrap">
      {filters.name ? (
        <FilterBadge active labelPrefix="Booking Name: " label={filters.name} onRemove={() => handleRemoveName()} />
      ) : null}
      {filters.lotNumber ? (
        <FilterBadge
          active
          labelPrefix="Lot No.: "
          label={filters.lotNumber}
          onRemove={() => handleRemoveLotNumber()}
        />
      ) : null}
      {filters.accountNumber ? (
        <FilterBadge
          active
          labelPrefix="Contract No.: "
          label={filters.accountNumber}
          onRemove={() => handleRemoveAccountNumber()}
        />
      ) : null}
      {filters.dateOpenedAfter && filters.dateOpenedBefore ? (
        <FilterBadge
          active
          label={`${toDisplayDate(filters.dateOpenedAfter, "MMM d")} - ${toDisplayDate(filters.dateOpenedBefore, "MMM d")}`}
          onRemove={handleRemoveDateRange}
        />
      ) : null}
      <FilterBadgeGroup
        groupLabel="Status"
        items={getFilterBadgeItems({
          options: statusOptions,
          activeFilters: filters.status,
          inactiveFilters: inactiveFilters.status,
        })}
        onRemove={handleRemoveValue("status")}
        onToggle={handleToggleValue("status")}
        onRemoveAll={handleRemoveAll("status")}
      />
      <FilterBadgeGroup
        groupLabel="Source"
        labelPrefix="Source: "
        items={getFilterBadgeItems({
          options: partnerOptions,
          activeFilters: filters.sourceIds,
          inactiveFilters: inactiveFilters.sourceIds,
        })}
        onRemove={handleRemoveValue("sourceIds")}
        onToggle={handleToggleValue("sourceIds")}
        onRemoveAll={handleRemoveAll("sourceIds")}
      />
      <FilterBadgeGroup
        groupLabel="Destination"
        labelPrefix="Destination: "
        items={getFilterBadgeItems({
          options: partnerOptions,
          activeFilters: filters.destinationIds,
          inactiveFilters: inactiveFilters.destinationIds,
        })}
        onRemove={handleRemoveValue("destinationIds")}
        onToggle={handleToggleValue("destinationIds")}
        onRemoveAll={handleRemoveAll("destinationIds")}
      />
    </div>
  );
}

export const supplyChainBookingsQuery = graphql(
  `
    query SupplyChainBookings(
      $first: Int
      $last: Int
      $before: String
      $after: String
      $businessUnitIds: [ID!]
      $sortBy: [SupplyChainBookingsColumnSort!]
      $plannedShippingDateBefore: Date
      $plannedShippingDateAfter: Date
      $status: [SupplyChainBookingStatusEnumType!]
      $name: String
      $lotNumber: String
      $accountNumberIstartswith: String
      $sourceIn: [UUID]
      $destinationIn: [UUID]
    ) {
      supplyChainBookings(
        first: $first
        last: $last
        before: $before
        after: $after
        owners: $businessUnitIds
        sortBy: $sortBy
        plannedShippingDateBefore: $plannedShippingDateBefore
        plannedShippingDateAfter: $plannedShippingDateAfter
        status: $status
        nameIstartswith: $name
        lotNumber: $lotNumber
        accountNumberIstartswith: $accountNumberIstartswith
        sourceIn: $sourceIn
        destinationIn: $destinationIn
      ) {
        numSupplyChainBookings
        pageInfo {
          ...PageInfo
        }
        edges {
          node {
            id
            name
            plannedShippingDate
            numberOfHead
            numNonArchivedDocuments
            status
            createdAt
            source {
              id
              supplyChainPartner {
                id
                businessName
              }
              address {
                city
                state
              }
            }
            destination {
              id
              supplyChainPartner {
                id
                businessName
              }
              address {
                city
                state
              }
            }
            accountManager {
              user {
                id
                firstName
                lastName
              }
            }
            # Metadata fields
            lotNumber
            penNumber
            accountNumber
            sexClass
            confirmed
            accreditations {
              id
              status
              label
            }
            labels {
              id
              status
              label
            }
            condition
            conditions
            ownershipType
            breedNote
            dateOfSale
            dateOfVisit
            projectedOutDate
            dateDelivered
            price
            freightCost
            slide
            purchasePricePerCwt
            riskManagement
            shippedWeight
            baseWeight
            estimatedChoicePercent
            shrinkPercent
            estimatedWeightNote
            sourceNote
            brandNote
            certificateNumber
            program {
              id
              label
            }
            grid {
              id
              label
            }
            truckingCompany {
              id
              businessName
            }
            broker {
              id
              businessName
            }
            veterinarian {
              id
              user {
                id
                firstName
                lastName
              }
            }
            serviceProvided
          }
        }
      }
    }
  `,
  [pageInfoFragment],
);
